import React, { useMemo, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import PropTypes from 'prop-types';
import styled, { css, useTheme } from 'styled-components';

import goodApi from '@fo-apis/good/goodApi';
import useOptionCountValidator from '@ecp/common/src/hooks/good/option/useGoodOptionValidator';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { FlexBox, FlexBoxColumn, FlexBoxSpaceBetween, HorizontalDivider } from '@ecp/common/src/layouts/flex/styled';
import { TextBox } from '@ecp/common/src/text/TextBox';
import NumberIndicator from '@ecp/common/src/components/input/NumberIndicator';
import { ReactComponent as ClearSvg } from '@ecp/common/src/assets/icon/input__clear.svg';

import { goodOptionListState } from '@fo-recoil/good/atom';
import useSiteInfo from '@fo-hooks/common/useSiteInfo';
import { useAlertDialog } from '@fo-hooks/common/useModal';
import { useDisplayPrice } from '@fo-hooks/price/DisplayPriceHook';

const OptionPriceUnitInfo = ({ title }) => {
  const theme = useTheme();
  return (
  <FlexBox>
    <TextBox color={theme.color.text.sub} size={theme.font.size.small} height={theme.font.lineHeight.demiLight}>
      {title}
    </TextBox>
    <Spacing left={12} />
  </FlexBox>
)};

OptionPriceUnitInfo.propTypes = {
  title: PropTypes.string,
};

const GoodSelectedOption = ({
  option,
  floatingType,
  showDivider,
  isGroup,
  isRental,
  isRegularDelivery,
  stockQtyDisplay,
  isPriceOpen,
  showBtnBulkp,
  isCartPopup,
  index,
  selectedOptionList,
  ordPossibleMaxQtyStandardCode,
  ordPossibleMaxQty,
  ordPossibleMinQty,
  ordPossibleMaxAmount,
  ordPossibleMaxFormattedAmount,
  ordPossibleMinAmount,
  ordPossibleMinFormattedAmount,
  bulkpOrdPossibleMinQty,
}) => {
  const theme = useTheme();
  const { priceFormat } = useSiteInfo();
  const setOptionList = useSetRecoilState(goodOptionListState);
  const { showAlertDialog } = useAlertDialog();
  const { optionCountValidator } = useOptionCountValidator(showAlertDialog);
  const [ alertMessage, setAlertMessage ] = useState('');

  const handleCountChange = async(e, option) => {
    const newCount = Number(e?.target?.value);
    
    const selectedItemList = selectedOptionList?.map((opt) => ({
      goodNumber: opt?.goodNumber,
      itemNumber: opt?.itemNumber,
      orderQuantity: (opt.itemNumber === option?.itemNumber && opt.goodNumber === option?.goodNumber) ? newCount : opt?.count,
      orderAmount: opt?.discountPrice * ((opt.itemNumber === option?.itemNumber && opt.goodNumber === option?.goodNumber) ? newCount : opt?.count),
      additionalItem: opt?.additionalItem,
      discountPrice: opt?.discountPrice,
      discountFormattedPrice: opt?.discountFormattedPrice,
    }));
    const { result: availableResult = [] } = await goodApi.checkOrderAvailable(selectedItemList);
    const { message = '', rollback = false, reAlert = true } = optionCountValidator({
      option,
      newCount,
      showBtnBulkp,
      availableResult,
      selectedOptionList: selectedItemList,
      ordPossibleMaxQtyStandardCode,
      ordPossibleMaxQty,
      ordPossibleMinQty,
      ordPossibleMaxAmount,
      ordPossibleMaxFormattedAmount,
      ordPossibleMinAmount,
      ordPossibleMinFormattedAmount,
      bulkpOrdPossibleMinQty,
      priceFormat
    });

    if (message) {
      if (!(message === alertMessage && !reAlert)) { //직전 노출된 알럿과 다르거나, 같아도 reAlert가 true인 경우 알럿 노출 
        showAlertDialog(message);
        setAlertMessage(message);
      }

      if (rollback) {
        return;
      }
    }
        
    setOptionList((prevList) =>
      prevList?.map((item) =>
        item.itemNumber === option?.itemNumber && item.goodNumber === option?.goodNumber
          ? { ...option, count: newCount, orderPrice: newCount * option?.discountPrice }
          : item
      )
    );
  };
  const handleClear = () => {
    setOptionList((prevList) =>
      prevList?.filter((it) => it.itemNumber !== option.itemNumber || it.goodNumber !== option.goodNumber)
    );
  };

  const displayOptionPrice = useDisplayPrice(
    Number(option?.discountPrice * option?.count).toLocaleString(),
    Number(option?.discountFormattedPrice * option?.count).toLocaleString()
  );

  const optionName = useMemo(
    () => (!isGroup && option?.itemNumber === '00000' ? '' : (isGroup || isCartPopup) ? option?.itemName : option?.itemNameView),
    [isGroup, option?.itemName, option?.itemNumber, option?.itemNameView]
  );

  const deletable = useMemo(
    () => isGroup || option?.multiItem || option?.additionalItem,
    [isGroup, option?.multiItem, option?.additionalItem]
  );

  const showStockQuantity = useMemo(() => {
    return option?.itemNumber === '00000' && option?.stockManagement && stockQtyDisplay;
  }, [option?.itemNumber, option?.stockManagement, stockQtyDisplay]);

  const showRental = useMemo(() => {
    return isRental && !option?.additionalItem && !floatingType;
  }, [floatingType, isRental, option?.additionalItem]);

  const showRegularDelivery = useMemo(() => {
    return isRegularDelivery && !option?.additionalItem && !floatingType;
  }, [floatingType, isRegularDelivery, option?.additionalItem]);

  return (
    <>
      {floatingType && showDivider && <HorizontalDivider minHeight={1} />}
      {isCartPopup && index === 0 && <Divider minHeight={2} />}
      <SelectedOptions floatingType={floatingType} isCartPopup={isCartPopup}>
        {optionName && (
          <FlexBox align-items={isCartPopup? 'center' : 'flex-start'}>
            {isCartPopup && (
              <Spacing right={10}>
                <TextBox style={{ minWidth: '52px', height: '59px', backgroundColor: theme.color.background.basic, justifyContent: 'center' }} weight={theme.font.weight.medium}>
                  선택{index+1}
                </TextBox>
              </Spacing>
            )}
            {option?.subGoodIndex && (
              <FlexBox>
                <TextBox
                  color={theme.color.primary}
                  weight={theme.font.weight.medium}
                  style={{ minWidth: 'max-content' }}
                >
                  {option?.subGoodIndex}
                </TextBox>
                <Spacing right={5} />
              </FlexBox>
            )}
            <OptionName isCartPopup={isCartPopup}>{optionName}</OptionName>
          </FlexBox>
        )}
        <FlexBoxSpaceBetween width={'100%'}>
          <FlexBox>
            <NumberIndicator
              fontWeight={theme.font.weight.medium}
              value={option?.count}
              minValue={1}
              onChange={(e) => handleCountChange(e, option)}
              disabled={!isPriceOpen}
            />
            {showStockQuantity && (
              <>
                <Spacing left={5} />
                <TextBox
                  size={theme.font.size.small}
                  weight={theme.font.weight.demiLight}
                >{`(재고 : ${option?.salePossibleQuantity} 개)`}</TextBox>
              </>
            )}
          </FlexBox>
          <FlexBox align-items={'flex-end'}>
            {isPriceOpen && (
              <>
                {showRental && <OptionPriceUnitInfo title={'월 렌탈료'} />}
                {showRegularDelivery && <OptionPriceUnitInfo title={'1회당'} />}
                <TextBox height={'24px'} size={theme.font.size.table} weight={theme.font.weight.medium}>
                  {`${displayOptionPrice}${priceFormat}`}
                </TextBox>
              </>
            )}
            {deletable && (
              <FlexBox height={'24px'}>
                <Spacing left={10} />
                <ClearIcon onClick={handleClear} />
              </FlexBox>
            )}
          </FlexBox>
        </FlexBoxSpaceBetween>
      </SelectedOptions>
    </>
  );
};

GoodSelectedOption.propTypes = {
  option: PropTypes.object,
  floatingType: PropTypes.bool,
  showDivider: PropTypes.bool,
  isGroup: PropTypes.bool,
  isRental: PropTypes.bool,
  isRegularDelivery: PropTypes.bool,
  stockQtyDisplay: PropTypes.bool,
  isPriceOpen: PropTypes.bool,
  itemNameView: PropTypes.string,
  showBtnBulkp: PropTypes.bool,
  isCartPopup: PropTypes.bool,
  index: PropTypes.number,
  selectedOptionList: PropTypes.array,
  ordPossibleMaxQtyStandardCode: PropTypes.string,
  ordPossibleMaxQty: PropTypes.number,
  ordPossibleMinQty: PropTypes.number,
  ordPossibleMaxAmount: PropTypes.number,
  ordPossibleMaxFormattedAmount: PropTypes.number,
  ordPossibleMinAmount: PropTypes.number,
  ordPossibleMinFormattedAmount: PropTypes.number,
  bulkpOrdPossibleMinQty: PropTypes.number,
};

export default GoodSelectedOption;

const floatingTypeCss = css`
  padding: 20px 0;
  background-color: transparent;
  height: auto;
`;

const cartPopupTypeCss = css`
  flex-direction: row;
  align-items: center;
  width: 100%;
  min-height: 59px;
  border-bottom: 1px solid ${(props) => props.theme.color.line.basic};
  padding: 0;
`;

const SelectedOptions = styled(FlexBoxColumn)`
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  background-color: ${(props) => props.theme.color.background.sub};
  padding: 25px 30px;
  border-radius: ${(props) => props.theme.component.good.selectedOption.borderRadius};

  ${({ floatingType }) => floatingType && floatingTypeCss}

  ${({ isCartPopup }) => isCartPopup && cartPopupTypeCss}
`;

const ClearIcon = styled(ClearSvg)`
  cursor: pointer;
  width: 16px;
  height: 16px;
`;

const OptionName = styled(TextBox)`
  word-break: keep-all;

  ${({ isCartPopup }) => isCartPopup && `min-width: 160px; word-break: break-all;`}
`

const Divider = styled(HorizontalDivider)`
  background-color: #000000;
`
